import type { SearchProfile } from "@/local-shared/model";
import DefaultAvatar from "@/assets/img/r-icon.svg";
import VerfiedBadge from "@/assets/img/symbolic/verified-badge.svg?react";
import { useContext } from "react";
import { AuthContext } from "@/Realize";
import UIListener from "@/pages/home/UIListener";
import { motion } from "framer-motion";
import TwitchIcon from "@/assets/img/icons/twitch.svg?react";

const MAX_USERNAME_LENGTH = 20;

const maxUserName = (username: string): string =>
{
    if (username.length > MAX_USERNAME_LENGTH)
    {
        return `${username.slice(0, MAX_USERNAME_LENGTH)}...`;
    }

    return username;
};

const maxBioLength = (bio: string | null): string | null =>
{
    if (!bio) return null;

    const MAX_BIO_LENGTH = 35;

    if (bio.length > MAX_BIO_LENGTH)
    {
        return `${bio.slice(0, MAX_BIO_LENGTH)}...`;
    }

    return bio;
};

export function ProfileCardSkeleton()
{
    return (
        <div
            className={
                Wind.style({
                    padding: "p-3",
                    display: "flex",
                    flexDirection: "flex-row",
                    alignItems: "items-center",
                    gap: "gap-3",
                    borderBottomWidth: "border-b",
                    borderColor: "border-[#6C3331]/20",
                }).class
            }
        >
            {/* Profile Image Placeholder */}
            <div
                className={
                    Wind.style({
                        width: "w-10",
                        height: "h-10",
                        borderRadius: "rounded-full",
                    }).class + " animated-gradient shimmer"
                }
            />

            {/* Text Placeholder */}
            <div className="flex flex-col justify-center">
                <div className="h-4 w-32 animate-pulse mb-2 rounded animated-gradient shimmer" />
                <div className="h-3 w-24 animate-pulse rounded animated-gradient shimmer" />
            </div>
        </div>
    );
}

const animationVariants = {
    initial: {
        x: "100%",
    },
    animate: {
        x: "0%",
    },
};

export default function SearchProfile({
    account,
    index,
    onProfileClicked,
}: {
    account: SearchProfile;
    index: number;
    onProfileClicked: (profileId: number) => void;
})
{
    const userData = useContext(AuthContext);

    return (
        <motion.div
            key={`${account.id}-${index}`}
            className={
                Wind.style({
                    padding: "p-3",
                    display: "flex",
                    flexDirection: "flex-row",
                    alignItems: "items-center",
                    gap: "gap-3",
                    borderBottomWidth: "border-b",
                    borderColor: "border-[#6C3331]/20",
                }).class
            }
            style={{
                willChange: "transform",
            }}
            onClick={(e) =>
            {
                e.stopPropagation();
                onProfileClicked(account.id);

                if (account.id.toString() !== userData?.id.toString())
                {
                    UIListener.trigger(
                        "updateSelectedProfile",
                        account.photo_path
                    );
                }
            }}
            variants={animationVariants}
            initial={"initial"}
            whileInView={"animate"}
            viewport={{
                once: true,
            }}
        >
            <img
                src={account.photo_path ?? DefaultAvatar}
                alt={account.username}
                className={
                    Wind.style({
                        width: "w-10",
                        height: "h-10",
                        borderRadius: "rounded-full",
                        borderWidth: "border",
                        borderColor: "border-[#6C3331]/20",
                        objectFit: "object-cover",
                    }).class
                }
            />
            <div
                className={
                    Wind.style({
                        display: "flex",
                        flexDirection: "flex-col",
                        flex: "flex-1",
                    }).class
                }
            >
                <h2
                    className={
                        Wind.style({
                            fontFamily: "font-poppins",
                            fontSize: "text-base",
                            fontWeight: "font-bold",
                            lineHeight: "leading-5",
                            color: "text-[#3F1413]",
                            display: "flex",
                            gap: "gap-1",
                            alignItems: "items-center",
                        }).class
                    }
                >
                    {maxUserName(account.username)}
                    {" "}
                    {account.verified && <VerfiedBadge />}
                    {account.isTwitch && (
                        <TwitchIcon
                            className={
                                Wind.style({
                                    width: "w-4",
                                    height: "h-4",
                                    fill: "fill-[#6441A5]",
                                }).class
                            }
                        />
                    )}
                    {userData?.id.toString() === account.id.toString() ? (
                        <span className="text-[#6C3331] italic text-xs">
                            (YOU)
                        </span>
                    ) : null}
                </h2>
                <p
                    className={
                        Wind.style({
                            fontFamily: "font-poppins",
                            fontSize: "text-sm",
                            fontWeight: "font-normal",
                            lineHeight: "leading-4",
                            color: "text-[#3F1413]/70",
                        }).class
                    }
                >
                    {maxBioLength(account.bio) ?? (
                        <span className="italic">No bio available</span>
                    )}
                </p>
            </div>
        </motion.div>
    );
}
